/**
 * Módulo centralizado para formateo de fechas usando date-fns
 * SOLUCIÓN PERMANENTE: No depende de locale del sistema
 */

import {
  format,
  parseISO,
  isValid,
  formatDistanceToNow,
  isAfter,
  isBefore,
  parse
} from 'date-fns';
import { es } from 'date-fns/locale';
import { enUS } from 'date-fns/locale';
import i18n from '@/i18n';

/**
 * Retorna el locale de date-fns según el idioma seleccionado en i18n
 */
const getDateLocale = () => i18n.language?.startsWith('en') ? enUS : es;

/**
 * Convierte cualquier entrada a objeto Date válido
 */
const parseDate = (dateInput) => {
  if (!dateInput) return null;

  // Si ya es Date válido
  if (dateInput instanceof Date && isValid(dateInput)) {
    return dateInput;
  }

  // Si es string ISO
  if (typeof dateInput === 'string') {
    const parsed = parseISO(dateInput);
    if (isValid(parsed)) {
      return parsed;
    }

    // Intentar parseo en español ("2 de diciembre de 2024")
    const naturalParsedEs = parse(dateInput, "d 'de' MMMM 'de' yyyy", new Date(), { locale: es });
    if (isValid(naturalParsedEs)) {
      return naturalParsedEs;
    }

    // Intentar parseo en inglés ("December 2, 2024")
    const naturalParsedEn = parse(dateInput, 'MMMM d, yyyy', new Date(), { locale: enUS });
    if (isValid(naturalParsedEn)) {
      return naturalParsedEn;
    }

    return null;
  }

  // Si es timestamp
  if (typeof dateInput === 'number') {
    const date = new Date(dateInput);
    return isValid(date) ? date : null;
  }

  return null;
};

/**
 * Formatea fecha completa: "2 de diciembre de 2024"
 */
export const formatDate = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'N/A';

  const loc = getDateLocale();
  const pattern = loc === enUS ? 'MMMM d, yyyy' : "d 'de' MMMM 'de' yyyy";
  return format(date, pattern, { locale: loc });
};

/**
 * Formatea fecha y hora: "2 dic 2024, 14:30"
 */
export const formatDateTime = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'N/A';

  return format(date, "d MMM yyyy, HH:mm", { locale: getDateLocale() });
};

/**
 * Formatea fecha y hora compacta para tablas: "02-Dic-24 14:30"
 * Ideal para columnas con espacio limitado
 */
export const formatCompactDateTime = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return '-';

  // Mes abreviado con primera letra mayúscula
  const monthStr = format(date, 'MMM', { locale: getDateLocale() });
  const monthCapitalized = monthStr.charAt(0).toUpperCase() + monthStr.slice(1);

  return format(date, `dd-'${monthCapitalized}'-yy HH:mm`);
};

/**
 * Formatea fecha corta: "02/12/2024"
 */
export const formatShortDate = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'N/A';

  const loc = getDateLocale();
  const pattern = loc === enUS ? 'yyyy/MM/dd' : 'dd/MM/yyyy';
  return format(date, pattern);
};

/**
 * Formatea fecha y hora corta: "02/12/2024 14:30"
 */
export const formatShortDateTime = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'N/A';

  return format(date, 'dd/MM/yyyy HH:mm');
};

/**
 * Formatea hora: "14:30"
 */
export const formatTime = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'N/A';

  return format(date, 'HH:mm');
};

/**
 * Formatea distancia relativa: "hace 2 horas"
 */
export const formatRelative = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'N/A';

  return formatDistanceToNow(date, {
    addSuffix: true,
    locale: getDateLocale()
  });
};

/**
 * Formatea para inputs HTML: "2024-12-02"
 */
export const formatForInput = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return '';

  return format(date, 'yyyy-MM-dd');
};

/**
 * Formatea fecha para envío a API: "2024-12-02" o null
 * Soporta objetos dayjs (de Arco DatePicker), Date, strings ISO y timestamps
 */
export const formatDateForApi = (dateInput) => {
  if (!dateInput) return null;
  if (typeof dateInput === 'object' && typeof dateInput.format === 'function') {
    const formatted = dateInput.format('YYYY-MM-DD');
    return formatted === 'Invalid Date' ? null : formatted;
  }
  const date = parseDate(dateInput);
  if (!date) return null;
  return format(date, 'yyyy-MM-dd');
};

/**
 * Formatea mes y año: "Diciembre 2024"
 */
export const formatMonthYear = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'N/A';

  return format(date, 'MMMM yyyy', { locale: getDateLocale() });
};

/**
 * Utilidades adicionales
 */
export const isValidDate = (dateInput) => {
  return parseDate(dateInput) !== null;
};

export const isDateAfter = (date1, date2) => {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);
  return d1 && d2 ? isAfter(d1, d2) : false;
};

export const isDateBefore = (date1, date2) => {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);
  return d1 && d2 ? isBefore(d1, d2) : false;
};
